import { apiClient } from '@/shared/api/api.client'

// ─── Type definitions ───────────────────────────────────────────────────────

export interface ClientRecord {
	clientId: string
	name: string
	code: string
	parentClientId: string | null
	productiveCompanyId: string | null
	legalEntity: string | null
	countryRegion: string | null
	isActive: boolean
	createdAt: string
	updatedAt: string
	deletedAt: string | null
}

export interface ProjectRecord {
	projectId: string
	clientId: string
	subClientId: string | null
	code: string
	displayName?: string
	description?: string
	isActive: boolean
	ownerEmail?: string
	pmEmail?: string
	validEnvs?: string[]
	createdAt: string
	updatedAt: string
	deletedAt: string | null
}

export interface ProjectElementRecord {
	elementId: string
	projectId: string
	elementType: string
	name: string
	description?: string
	environment?: string
	url?: string
	isActive: boolean
	createdAt: string
	updatedAt: string
	deletedAt: string | null
}

export interface ProjectElementFormData {
	elementType: string
	name: string
	description?: string
	environment?: string
	url?: string
	isActive?: boolean
}

export interface ClientCreateInput {
	name: string
	code: string
	parentClientId?: string | null
	productiveCompanyId?: string | null
	legalEntity?: string | null
	countryRegion?: string | null
}

export interface ProjectCreateInput {
	clientId: string
	subClientId?: string | null
	code: string
	displayName?: string
	ownerEmail?: string
	pmEmail?: string
	validEnvs?: string[]
}

// ─── API surface ────────────────────────────────────────────────────────────

export const organizationApi = {
	// Clients
	listClients: async (): Promise<ClientRecord[]> => {
		const response = await apiClient.get<ClientRecord[]>('/clients')
		return Array.isArray(response.data) ? response.data : []
	},
	createClient: async (data: ClientCreateInput): Promise<ClientRecord> => {
		const response = await apiClient.post<ClientRecord>('/clients', data)
		return response.data
	},
	updateClient: async ({ id, data }: { id: string; data: Partial<ClientRecord> }): Promise<ClientRecord> => {
		const response = await apiClient.put<ClientRecord>(`/clients/${id}`, data)
		return response.data
	},
	deleteClient: async (id: string): Promise<void> => {
		await apiClient.delete(`/clients/${id}`)
	},

	// Projects
	listProjects: async (): Promise<ProjectRecord[]> => {
		const response = await apiClient.get<ProjectRecord[]>('/projects')
		return response.data || []
	},
	getProject: async (projectId: string): Promise<ProjectRecord> => {
		const response = await apiClient.get<ProjectRecord>(`/projects/${projectId}`)
		return response.data
	},
	createProject: async (data: ProjectCreateInput): Promise<ProjectRecord> => {
		const response = await apiClient.post<ProjectRecord>('/projects', data)
		return response.data
	},
	updateProject: async ({ id, data }: { id: string; data: Partial<ProjectRecord> }): Promise<ProjectRecord> => {
		const response = await apiClient.put<ProjectRecord>(`/projects/${id}`, data)
		return response.data
	},
	deleteProject: async (id: string): Promise<void> => {
		await apiClient.delete(`/projects/${id}`)
	},

	// Project Elements
	listProjectElements: async (projectId: string): Promise<ProjectElementRecord[]> => {
		const response = await apiClient.get<ProjectElementRecord[]>(`/projects/${projectId}/elements`)
		return response.data || []
	},
	createProjectElement: async ({
		projectId,
		data,
	}: {
		projectId: string
		data: ProjectElementFormData
	}): Promise<ProjectElementRecord> => {
		const response = await apiClient.post<ProjectElementRecord>(`/projects/${projectId}/elements`, data)
		return response.data
	},
	updateProjectElement: async ({
		projectId,
		elementId,
		data,
	}: {
		projectId: string
		elementId: string
		data: Partial<ProjectElementFormData>
	}): Promise<ProjectElementRecord> => {
		const response = await apiClient.put<ProjectElementRecord>(`/projects/${projectId}/elements/${elementId}`, data)
		return response.data
	},
	deleteProjectElement: async ({ projectId, elementId }: { projectId: string; elementId: string }): Promise<void> => {
		await apiClient.delete(`/projects/${projectId}/elements/${elementId}`)
	},
}
